import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  Campaign,
  CampaignStatus,
  CampaignType,
  fetchCampaigns,
  deleteCampaign,
  scheduleCampaign,
  pauseCampaign,
  resumeCampaign,
  cancelCampaign,
  runCampaign,
  getStatusColor,
  getTypeColor,
} from '../services/campaignApi';

const Campaigns: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  // Filters
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<CampaignType | ''>('');

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchCampaigns({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        page: pagination.page,
        limit: pagination.limit,
      });
      setCampaigns(response.campaigns);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, pagination.page, pagination.limit]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleAction = async (action: string, campaign: Campaign) => {
    if (!campaign._id) return;

    try {
      switch (action) {
        case 'schedule':
          await scheduleCampaign(campaign._id);
          toast.success('Campaign scheduled');
          break;
        case 'pause':
          await pauseCampaign(campaign._id);
          toast.success('Campaign paused');
          break;
        case 'resume':
          await resumeCampaign(campaign._id);
          toast.success('Campaign resumed');
          break;
        case 'cancel':
          if (window.confirm('Are you sure you want to cancel this campaign?')) {
            await cancelCampaign(campaign._id);
            toast.success('Campaign cancelled');
          }
          break;
        case 'run':
          const result = await runCampaign(campaign._id);
          toast.success(`Campaign triggered! Run ID: ${result.runId}`);
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this campaign?')) {
            await deleteCampaign(campaign._id);
            toast.success('Campaign deleted');
          }
          break;
      }
      loadCampaigns();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const brandGradient = 'from-[#FF9C15] to-[#F42026]';

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Header */}
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8'>
          <div>
            <h1 className='text-3xl font-bold text-white'>Campaigns</h1>
            <p className='text-gray-400 mt-1'>Manage your notification campaigns</p>
          </div>
          <button
            onClick={() => navigate('/campaigns/new')}
            className={`flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${brandGradient} text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200`}
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
            </svg>
            Create Campaign
          </button>
        </div>

        {/* Filters */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 mb-6'>
          <div className='flex flex-wrap gap-4'>
            <div>
              <label className='block text-sm text-gray-400 mb-1'>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | '')}
                className='px-4 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]'
              >
                <option value=''>All Statuses</option>
                <option value='DRAFT'>Draft</option>
                <option value='SCHEDULED'>Scheduled</option>
                <option value='RUNNING'>Running</option>
                <option value='PAUSED'>Paused</option>
                <option value='COMPLETED'>Completed</option>
                <option value='CANCELLED'>Cancelled</option>
              </select>
            </div>
            <div>
              <label className='block text-sm text-gray-400 mb-1'>Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as CampaignType | '')}
                className='px-4 py-2 bg-slate-800 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#FF9C15]'
              >
                <option value=''>All Types</option>
                <option value='ONE_TIME'>One-Time</option>
                <option value='RECURRING'>Recurring</option>
                <option value='TRIGGERED'>Triggered</option>
              </select>
            </div>
            <div className='flex items-end'>
              <button
                onClick={loadCampaigns}
                className='px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors'
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className='bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6'>
            <p className='text-red-400'>{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className='flex justify-center items-center py-20'>
            <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF9C15]'></div>
          </div>
        )}

        {/* Campaigns Table */}
        {!loading && campaigns.length === 0 && (
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center'>
            <svg className='w-16 h-16 mx-auto text-gray-500 mb-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' />
            </svg>
            <h3 className='text-xl font-semibold text-white mb-2'>No campaigns found</h3>
            <p className='text-gray-400 mb-6'>Create your first campaign to get started</p>
            <button
              onClick={() => navigate('/campaigns/new')}
              className={`px-6 py-3 bg-gradient-to-r ${brandGradient} text-white font-semibold rounded-lg`}
            >
              Create Campaign
            </button>
          </div>
        )}

        {!loading && campaigns.length > 0 && (
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden'>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='bg-white/5 border-b border-white/10'>
                    <th className='px-6 py-4 text-left text-sm font-semibold text-gray-300'>Name</th>
                    <th className='px-6 py-4 text-left text-sm font-semibold text-gray-300'>Type</th>
                    <th className='px-6 py-4 text-left text-sm font-semibold text-gray-300'>Status</th>
                    <th className='px-6 py-4 text-left text-sm font-semibold text-gray-300'>Stats</th>
                    <th className='px-6 py-4 text-left text-sm font-semibold text-gray-300'>Next Run</th>
                    <th className='px-6 py-4 text-right text-sm font-semibold text-gray-300'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr
                      key={campaign._id}
                      className='border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer'
                      onClick={() => navigate(`/campaigns/${campaign._id}`)}
                    >
                      <td className='px-6 py-4'>
                        <div>
                          <div className='font-medium text-white'>{campaign.name}</div>
                          {campaign.description && (
                            <div className='text-sm text-gray-400 truncate max-w-xs'>{campaign.description}</div>
                          )}
                        </div>
                      </td>
                      <td className='px-6 py-4'>
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full text-white ${getTypeColor(campaign.type)}`}>
                          {campaign.type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className='px-6 py-4'>
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full text-white ${getStatusColor(campaign.status)}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className='px-6 py-4'>
                        <div className='text-sm text-gray-300'>
                          <div>Sent: {campaign.stats?.totalSent ?? 0}</div>
                          <div>Delivered: {campaign.stats?.totalDelivered ?? 0}</div>
                        </div>
                      </td>
                      <td className='px-6 py-4 text-sm text-gray-300'>
                        {formatDate(campaign.nextRunAt)}
                      </td>
                      <td className='px-6 py-4 text-right' onClick={(e) => e.stopPropagation()}>
                        <div className='flex justify-end gap-2'>
                          {/* Duplicate button - always visible */}
                          <button
                            onClick={() => navigate('/campaigns/new', { state: { duplicateFrom: campaign } })}
                            className='p-2 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-lg transition-colors'
                            title='Duplicate'
                          >
                            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' />
                            </svg>
                          </button>
                          {campaign.status === 'DRAFT' && (
                            <>
                              <button
                                onClick={() => navigate(`/campaigns/${campaign._id}/edit`)}
                                className='p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors'
                                title='Edit'
                              >
                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleAction('schedule', campaign)}
                                className='p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors'
                                title='Schedule'
                              >
                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleAction('delete', campaign)}
                                className='p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors'
                                title='Delete'
                              >
                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                                </svg>
                              </button>
                            </>
                          )}
                          {campaign.status === 'SCHEDULED' && (
                            <>
                              <button
                                onClick={() => handleAction('run', campaign)}
                                className='p-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-lg transition-colors'
                                title='Run Now'
                              >
                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' />
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleAction('pause', campaign)}
                                className='p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 rounded-lg transition-colors'
                                title='Pause'
                              >
                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z' />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleAction('cancel', campaign)}
                                className='p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors'
                                title='Cancel'
                              >
                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                                </svg>
                              </button>
                            </>
                          )}
                          {campaign.status === 'RUNNING' && (
                            <button
                              onClick={() => handleAction('pause', campaign)}
                              className='p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 rounded-lg transition-colors'
                              title='Pause'
                            >
                              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z' />
                              </svg>
                            </button>
                          )}
                          {campaign.status === 'PAUSED' && (
                            <>
                              <button
                                onClick={() => navigate(`/campaigns/${campaign._id}/edit`)}
                                className='p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors'
                                title='Edit'
                              >
                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleAction('resume', campaign)}
                                className='p-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-lg transition-colors'
                                title='Resume'
                              >
                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' />
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleAction('cancel', campaign)}
                                className='p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors'
                                title='Cancel'
                              >
                                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                                </svg>
                              </button>
                            </>
                          )}
                          {campaign.status === 'CANCELLED' && (
                            <button
                              onClick={() => handleAction('delete', campaign)}
                              className='p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors'
                              title='Delete'
                            >
                              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className='flex justify-between items-center px-6 py-4 border-t border-white/10'>
                <div className='text-sm text-gray-400'>
                  Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </div>
                <div className='flex gap-2'>
                  <button
                    onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                    disabled={pagination.page === 1}
                    className='px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors'
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                    className='px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors'
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Campaigns;
